import { Callout, DatePicker, Modal, Tag } from '@wfp/react';
import { format, isValid } from 'date-fns';
import React, { FC, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';

import ConfirmationModal from 'src/components/ConfirmationModal';
import { CALENDAR_DATE_FORMAT, NEW_DATE_FORMAT } from 'src/constants';
import { requestAPI } from 'src/redux/api/actions';
import {
  GET_ALL_REVIEW_STATE_DATA,
  GET_APP_CONFIGURATION,
} from 'src/redux/constants';
import { updateReviewData } from 'src/redux/review/actions';
import { RootState } from 'src/redux/store';

interface IOption {
  value: string;
  label: string;
}

interface Props {
  reportPk: number;
  hasPermissionToModify: boolean;
  setShowModal: (val: boolean) => void;
}

const ReviewGroupModal: FC<Props> = ({
  reportPk,
  hasPermissionToModify,
  setShowModal,
}) => {
  const [deadline, setDeadline] = useState(null);
  const [reviewState, setReviewState] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const reviewStateData = useSelector(
    (state: RootState) => state.viewData[GET_ALL_REVIEW_STATE_DATA],
  );
  const userData = useSelector(
    (state: RootState) => state.viewData?.[GET_APP_CONFIGURATION]?.data,
  );
  const isHqAdmin = userData.userInfo?.roles.includes('hq_admin');
  const dispatch = useDispatch();

  useEffect(() => {
    if (reviewStateData && reviewStateData.data) {
      if (reviewStateData.data.active_choice) {
        setReviewState(reviewStateData.data.active_choice);
      }
      if (reviewStateData.data.deadline) {
        setDeadline(new Date(reviewStateData.data.deadline));
      } else {
        setDeadline(null);
      }
    }
  }, [reviewStateData]);

  const options: IOption[] = reviewStateData?.data?.choices
    .slice()
    .sort((a: string, b: string) => a.localeCompare(b))
    .filter(
      (option: string) =>
        option !== 'Technical Units' ||
        (option === 'Technical Units' && isHqAdmin),
    )
    .map((option: string) => ({
      label: option,
      value: option,
    }));

  useEffect(() => {
    dispatch(
      requestAPI(GET_ALL_REVIEW_STATE_DATA, {
        url_format: { _id_: reportPk },
      }),
    );
  }, [reportPk]);

  function submitReviewData() {
    if (!reviewStateData.data.notifications_info) return;

    dispatch(
      updateReviewData({
        choice: reviewState,
        deadline: deadline ? format(deadline, NEW_DATE_FORMAT) : '',
        reportPk,
        notifications_info: reviewStateData.data.notifications_info,
      }),
    );
  }

  function handleSubmit() {
    if (reviewState === 'Technical Units' && deadline) {
      setShowConfirmationModal(true);
    } else {
      submitReviewData();
      setShowModal(false);
    }
  }

  function getOptionValue() {
    return { label: reviewState, value: reviewState };
  }

  const formatOptionLabel = ({ value, label }) => (
    <>
      {label}
      {reviewStateData?.data &&
        reviewStateData.data?.active_choice === value && (
          <Tag type="info" className="review-group-modal-current">
            Current reviewer
          </Tag>
        )}
    </>
  );

  const reviewMessage = useMemo(() => {
    if (reviewState !== 'Technical Units') {
      return 'Selected review group will not be notified by automatic email. Please inform them of the review status update and the desired due date.';
    }

    if (!deadline) {
      return (
        <>
          Selected review group will not be notified by automatic email{' '}
          <strong>unless you select a due date.</strong>
        </>
      );
    }

    return reviewStateData?.data?.notifications_info.hq_start > 0
      ? 'Technical Units will be notified by automatic email to finalize their review by the selected due date.'
      : 'Technical Units will be notified by automatic email to start their review and complete it by the selected due date.';
  }, [reviewState, deadline, reviewStateData?.data?.notifications_info]);

  return (
    <>
      {!showConfirmationModal && (
        <Modal
          modalHeading="Set review group"
          className="set-review-status-modal-content"
          open={true}
          onRequestClose={() => setShowModal(false)}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={!hasPermissionToModify}
          primaryButtonText="Set review group"
          onRequestSubmit={handleSubmit}
        >
          <div
            className="modal fade text-left"
            id="set-review-status-modal-acr"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-body">
                  <div className="review-group-modal-subtitle">
                    Select who should review the ACR and the desired due date.
                  </div>

                  <div className="set-review-status-modal-form">
                    <form>
                      <div className="row">
                        <span className="set-review-status-modal-review-group-title">
                          Review group
                        </span>
                        <div className="col-lg-6 review_state_radio">
                          <Select
                            classNamePrefix="wfp--react-select"
                            onChange={(val: IOption) => {
                              setReviewState(val.value);
                            }}
                            value={getOptionValue()}
                            formatOptionLabel={formatOptionLabel}
                            name="bureauFilters"
                            id="bureauFilter"
                            options={options}
                            isDisabled={reviewStateData?.isFetching}
                          />
                        </div>
                      </div>
                      <div className="set-review-status-modal-datepicker-container">
                        <span className="set-review-status-modal-datepicker-title">
                          Due date
                        </span>
                        <span className="col-lg-2">
                          <DatePicker
                            datePicker={ReactDatePicker}
                            startDate={deadline}
                            setStartDate={date => {
                              let newDate = null;
                              if (date && isValid(date)) {
                                newDate = date;
                              }
                              setDeadline(newDate);
                            }}
                            datePickerProps={{
                              disabled:
                                reviewStateData?.isFetching ||
                                (!isHqAdmin &&
                                  reviewState === 'Technical Units'),
                              dateFormat: CALENDAR_DATE_FORMAT,
                              monthsShown: 2,
                              placeholderText: 'Date',
                              minDate: new Date(),
                            }}
                          />
                        </span>
                      </div>
                    </form>

                    <Callout
                      icon={
                        reviewState === 'Technical Units' && deadline ? (
                          <span className="review-icon icon-send-mail" />
                        ) : null
                      }
                      className="review-group-modal-callout"
                    >
                      {reviewMessage}
                    </Callout>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
      {showConfirmationModal && (
        <ConfirmationModal
          open={true}
          onSubmit={() => {
            submitReviewData();
            setShowModal(false);
          }}
          onClose={() => {
            setShowConfirmationModal(false);
          }}
          heading="Email sending confirmation"
          content={
            <div className="confirmation-sending-mail">
              An automatic email with the updated review status and the desired
              due date will be sent to all Technical Units.
              <br />
              <br />
              Do you want to continue?
            </div>
          }
          primaryButtonText="Send notification"
          secondaryButtonText="Cancel"
          customClassName="review-group-modal-confirmation"
        />
      )}
    </>
  );
};

export default ReviewGroupModal;
